import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import INTProductDetailsDescription from "@tIntermatic/Components/INTProductDetailsDescription";
import ProductListActions from "@tIntermatic/Overrides/Widgets/ProductList/ProductListActions";
import INTProductCompare from "@tIntermatic/Widgets/ProductList/INTProductCompare";
import INTProductImage from "@tIntermatic/Widgets/ProductList/INTProductImage";
import INTPromoSlot from "@tIntermatic/Widgets/ProductList/INTPromoSlot";
import parse from "html-react-parser";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    currentLanguage: state.context.session.language,
    currentWebsiteName: state.context.website.name,
});

type Props = ProductCardSelections & HasProduct & ReturnType<typeof mapStateToProps>;

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoDesGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            padding: 0px 0;
            min-width: 320px; /* prevent overlap with second column */
            border: 1px solid #e1e1e1;
            background-color: #ffffff;
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 4, 4],
        css: css`
            padding-right: 20px;
            div[data-test-selector^="productListProductImage"] {
                height: 100%;
                justify-content: center;
                img {
                    min-width: 100%;
                    height: 228px;
                    object-fit: contain;
                    padding: 11.5px 0;
                }
            }
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 8, 8],
        css: css`
        padding-left:66px;
        > div[class^="GridWrapper"] {
            > div[class^="GridOffset"] {
                margin: 0px !important;
                width: 100% !important;
            }
        } 
        div[data-test-selector^="product-compare"] {
            padding-top: 18px;
                padding-bottom: 18px;
                text-align:left;
                width: 100%;
        }
        span[data-test-selector^="productNumber"] {
            font-size: 14px;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 17px;
            padding-top: 7px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding-top: 7px;
                            `,
                            css`
                                padding-top: 7px;
                            `,
                            css`
                                padding-top: 7px;
                            `,
                            css`
                                padding-top: 7px;
                            `,
                            css`
                                padding-top: 7px;
                            `,
                        ],
                        "min",
                    )}
        }
        a[data-test-selector^="productDescriptionLink"] {
            padding: 0;
            span {
                color: #33333a !important;
                font-size: 21px;
                font-weight: 600;
                letter-spacing: 0;
                line-height: 25px;
                &:hover {
                    color: #006bc0 !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding-top: 29.5px;
                                font-size: 21px;
                                font-weight: 600;
                                letter-spacing: 0;
                                line-height: 25px;
                                padding-right: 0;
                            `,
                            css`
                                padding-top: 29.5px;
                                font-size: 21px;
                                font-weight: 600;
                                letter-spacing: 0;
                                line-height: 25px;
                                padding-right: 0;
                            `,
                            css`
                                padding-top: 29.5px;
                                font-size: 21px;
                                font-weight: 600;
                                letter-spacing: 0;
                                line-height: 25px;
                                padding-right: 0;
                            `,
                            css`
                                padding-top: 29.5px;
                                font-size: 21px;
                                font-weight: 600;
                                letter-spacing: 0;
                                line-height: 25px;
                                padding-right: 35px;
                            `,
                            css`
                                padding-top: 29.5px;
                                font-size: 21px;
                                font-weight: 600;
                                letter-spacing: 0;
                                line-height: 25px;
                                padding-right: 35px;
                            `,
                        ],
                        "min",
                    )}
            }
        `,
    },
    infoGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0;
        `,
    },
    infoDesGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0;
            padding-top: 7px;
            padding-right: 35.5px;
        `,
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            padding: 0;
            padding-bottom: 53.5px;
            flex-basis: 52% !important;
            max-width: 52% !important;
        `,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    rightColumnGridContainer: {
        css: css`
            position: relative;
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    return (
        <GridContainer {...styles.gridContainer} data-test-selector={`productListProductCard${props.product.id}`}>
            <GridItem {...styles.leftColumnGridItem}>
                <INTProductImage showImage={props.showImage} />
            </GridItem>
            <GridItem {...styles.rightColumnGridItem}>
                <GridContainer {...styles.rightColumnGridContainer}>
                    {props.product.isSponsored && <INTPromoSlot isListView={true}></INTPromoSlot>}
                    <GridItem {...styles.infoGridItem}>
                        <ProductListProductInformation
                            showBrand={props.showBrand}
                            showTitle={props.showTitle}
                            showPartNumbers={props.showPartNumbers}
                            showAvailability={props.showAvailability}
                            showAttributes={props.showAttributes}
                        />
                    </GridItem>
                    <GridItem {...styles.infoDesGridItem}>
                        <INTProductDetailsDescription product={props.product} />
                    </GridItem>
                    <INTProductCompare showCompare={props.showCompare} />

                    {props.currentWebsiteName.toLowerCase() === "main" &&
                        props.currentLanguage?.languageCode.toLowerCase() === "en" && (
                            <GridItem {...styles.actionsGridItem}>
                                <ProductListActions
                                    showPrice={props.showPrice}
                                    showAvailability={props.showAvailability}
                                />
                            </GridItem>
                        )}
                    {/* <GridItem {...styles.searchScoreDataGridItem}>
                        <ProductSearchScoreData />
                    </GridItem> */}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProduct(ProductListProductCard));
